<template>
    <div class="table-responsive">
        <!-- <h5>All Records </h5> -->
        <div
            v-if="category == PHARMACY_ID"
            class="d-flex justify-content-end align-items-center mb-3"
        >
            <b-button
                id="recordFilterButton"
                @click="toggleFilterPopover"
                class="align-items-center border-secondary d-flex"
            >
                <i class="fa fa-filter"></i>
                <span v-if="appliedFiltersCount > 0" class="filter-badge">
                    {{ appliedFiltersCount }}</span
                >
            </b-button>

            <b-popover
                target="recordFilterButton"
                :show.sync="showFilterPopover"
                placement="bottomleft"
                triggers="manual"
                custom-class="record-filter-popover"
                ref="filterPopover"
            >
                <template #title>
                    <div
                        class="d-flex justify-content-between align-items-center"
                    >
                        <span>Filter Records</span>
                        <b-button
                            @click="showFilterPopover = false"
                            variant="link"
                            size="sm"
                            class="p-0 text-muted"
                        >
                            <i class="fa fa-times"></i>
                        </b-button>
                    </div>
                </template>
                <div class="pb-2">
                    <div
                        v-if="selectedRecordFilters.length > 0"
                        class="pb-2 text-right"
                    >
                        <span
                            @click="clearRecordFiltersAndClosePopover"
                            class="text-primary"
                            style="cursor: pointer; font-size: 0.85rem;"
                        >
                            Clear all
                        </span>
                    </div>
                    <treeselect
                        v-model="selectedRecordFilters"
                        :multiple="true"
                        :options="recordFilterOptions"
                        :value-consists-of="'LEAF_PRIORITY'"
                        :disable-branch-nodes="true"
                        placeholder="Select filters"
                        :default-expand-level="1"
                        @input="handleFilterUpdate"
                        :clearable="false"
                        :always-open="true"
                        class="record-filter-treeselect-in-popover"
                        :clearOnSelect="true"
                        :closeOnSelect="false"
                    />
                </div>
            </b-popover>
        </div>

        <loader v-if="isLoading" />

        <table
            v-else
            id="table-medical-records"
            class="table b-table list table-hover table-borderless"
        >
            <thead>
                <tr>
                    <th>Record:</th>
                    <th>Added:</th>
                    <th width="40%">Summary:</th>
                    <th>Reference:</th>
                    <th width="10%">Provider:</th>
                    <!-- 
                        EPIC-1000 : Medical Note migration 
                        column Naming differences for soap and patient as below
                    -->
                    <th width="10%">
                        {{ isSoap ? "Price:" : "Created By:" }}
                    </th>
                    <th>{{ isSoap ? "Billing:" : "Invoice:" }}</th>
                    <th></th>
                </tr>
            </thead>
            <tbody>
                <template v-for="item in list">
                    <row-record
                        v-if="item.module === 'record'"
                        :row="item"
                        :processing="selectedRowId"
                        :key="`record${item.id}`"
                        :data-record-id="item.id"
                        :content-expanded="contentExpandedId === item.id"
                        :isNewLabProfile="isNewLab"
                        @reload="getList"
                        @update-invoice-status="updateInvoiceStatus"
                        @delete="deleteRecord"
                        @view="viewRecord"
                        @edit="editRecord"
                        @duplicate="duplicateRecord"
                        @edit-diagnosis="editDiagnosis"
                        @refill="refillRecord"
                        @print="printLabel(item)"
                        @expand="expandSummary"
                        @history="viewHistoryRecord"
                        @add-addon-test="addAddonTest"
                        @decline-items="declinedItemRecord"
                    />
                    <row-notes
                        v-if="item.module === 'note'"
                        :row="item"
                        :key="`note-${item.id}`"
                        :data-record-id="item.id"
                        :content-expanded="contentExpandedId === item.id"
                        @edit="editNote(item)"
                        @delete="deleteNote(item)"
                        @duplicatenote="duplicateNote(item)"
                        @edit-diagnosis="editDiagnosis"
                        @expand="expandSummary"
                        :type="'record'"
                    />
                </template>
                <tr v-if="list.length === 0">
                    <td colspan="8">
                        <i>No records available</i>
                    </td>
                </tr>
            </tbody>
        </table>
        <history-pagination
            v-if="count"
            ref="historyPagination"
            :type="'record'"
            :category="category"
            :override-count="categoryRecordCount"
            @getList="getList"
        ></history-pagination>

        <modal-delete-record
            ref="modalDeleteRecord"
            @reload="onSuccess"
            :patientId="patientId"
        />
        <SoapDelete
            v-if="isSoapEnableBeta"
            ref="modalDeleteNote"
            @reload="getList"
            @success="reloadTabCount"
        />
        <modal-delete
            v-else
            ref="modalDeleteNote"
            @reload="getList"
            @success="reloadTabCount"
        />
        <modal-duplicate
            ref="modalDuplicateNote"
            @reload="getList"
            :patientId="patientId"
        />
        <modal-edit-diagnosis
            ref="modalEditDiagnosis"
            @onSuccess="$emit('open-diagnosis')"
            :patientId="patientId"
        />
        <modal-record ref="modalRecord" @onSuccess="onSuccess" />
        <modal-cage-card ref="modalCageCard" />
        <modal-view-record ref="modalViewRecord" />
        <modal-view-history ref="modalViewHistory" />
        <modal-add-addon-test ref="modalAddAddonTest" />
        <modal-decline-item
            ref="modalDeclineItem"
            modalRef="modalDeclineItem"
            :item="invoiceItem"
            @reload="getList"
        />
    </div>
</template>
<script>
import Records from "@/services/records";
import History from "@/services/patient-history";
import Loader from "@/components/elements/loader-list";
import RowRecord from "@/components/medical-records/record-row";
import AccountService from "@/services/account.js";
import EXTENSIONS from "@/constants/extensions";
import { find, debounce } from "lodash";
import Treeselect from "@riophae/vue-treeselect";
import axios from "axios";
// import "@riophae/vue-treeselect/dist/vue-treeselect.css";

const PHARMACY = 3;

export default {
    components: {
        Loader,
        RowRecord,
        Treeselect,

        RowNotes: () => import("@/components/medical-notes/note-row"),
        ModalDelete: () =>
            import("@/components/medical-notes/note-delete-modal"),
        SoapDelete: () => import("@/components/soap/soap-delete"),
        ModalDuplicate: () =>
            import("@/components/medical-notes/note-duplicate-modal"),
        ModalEditDiagnosis: () =>
            import("@/components/commons/modal-diagnosis"),
        ModalDeleteRecord: () =>
            import("@/components/medical-records/record-delete-modal"),
        ModalRecord: () => import("@/module/commons/record/create-update"),
        ModalCageCard: () => import("@/module/schedule/appointment/cage-card"),
        ModalViewRecord: () => import("@/components/medical-records/view"),
        ModalViewHistory: () => import("@/components/medical-records/history"),
        HistoryPagination: () =>
            import(
                "@/components/patient-history-pagination/history-pagination"
            ),
        ModalAddAddonTest: () =>
            import("@/components/patient-lab/add-addon-test-modal"),
        ModalDeclineItem: () =>
            import("@/components/invoice-item/item-decline-modal")
    },
    props: {
        category: {
            type: [Number, String],
            default: null
        },
        patientDetails: {
            type: Object
        }
    },
    data() {
        return {
            list: [],
            selectedRowId: null,
            isLoading: false,
            meta: null,
            contentExpandedId: null,
            isNewLab: false,
            PHARMACY_ID: PHARMACY,
            invoiceItem: {},
            selectedRecordFilters: [],
            apiCancelToken: null,
            debouncedGetList: null,
            dispensingStatuses: [],
            showFilterPopover: false,
            categoryRecordCount: null,
            clickOutsideTimeout: null,
            isFilterChanging: false
        };
    },
    computed: {
        isSoapEnableBeta() {
            return this.$store.getters["soap/isSoapEnableBeta"];
        },
        isSoap() {
            return this.$route.name === "soap";
        },
        soapDetails() {
            if (this.isSoap) {
                return this.$store.getters["soap/soapDetails"];
            } else {
                return null;
            }
        },
        patientId() {
            return this.patientDetails ? this.patientDetails.id : null;
        },
        count() {
            return this.isSoap
                ? this.$store.state.soap.countsSoap
                : this.$store.state.patient.counts;
        },
        vetsourceEnabled() {
            return this._isExtensionEnabled(EXTENSIONS.CODE.CODE_VETSOURCE);
        },
        cubexEnabled() {
            return this._isExtensionEnabled(EXTENSIONS.CODE.CODE_CUBEX);
        },
        vetcoveEnabled() {
            return this._isExtensionEnabled(
                EXTENSIONS.CODE.CODE_VETCOVE_ONLINE
            );
        },
        recordFilterOptions() {
            let options = [];
            const sourceChildren = [];

            // Get currently selected status filters
            const selectedStatuses = this.selectedRecordFilters
                .filter(f => f.startsWith("status|"))
                .map(f => f.split("|")[1]);

            // Source options - only if category is Pharmacy
            if (this.category == this.PHARMACY_ID) {
                // If any status is selected, only show Cubex
                if (selectedStatuses.length > 0) {
                    if (this.cubexEnabled) {
                        sourceChildren.push({
                            id: "source|CUBEX",
                            label: "Cubex"
                        });
                    }
                } else {
                    // Show all source options if no status is selected
                    sourceChildren.push({
                        id: "source|null",
                        label: "In House"
                    });

                    if (this.cubexEnabled) {
                        sourceChildren.push({
                            id: "source|CUBEX",
                            label: "Cubex"
                        });
                    }

                    if (this.vetsourceEnabled) {
                        sourceChildren.push({
                            id: "source|VETSOURCE",
                            label: "Online Vetsource"
                        });
                    }

                    if (this.vetcoveEnabled) {
                        sourceChildren.push({
                            id: "source|VETCOVE_ONLINE",
                            label: "Online Vetcove"
                        });
                    }
                }

                if (sourceChildren.length > 0) {
                    options.push({
                        id: "source",
                        label: "Source",
                        children: sourceChildren
                    });
                }
            }

            // Get currently selected source filters
            const selectedSources = this.selectedRecordFilters
                .filter(f => f.startsWith("source|"))
                .map(f => f.split("|")[1]);

            // Only show status options if no source is selected or if Cubex is selected
            const shouldShowStatus =
                selectedSources.length === 0 ||
                selectedSources.includes("CUBEX");

            if (shouldShowStatus) {
                // Status options from API
                const statusChildren = this.dispensingStatuses
                    .filter(status => status.record_count > 0)
                    .map(status => ({
                        id: `status|${status.id}`,
                        label: status.label
                    }));

                if (statusChildren.length > 0) {
                    options.push({
                        id: "status",
                        label: "Cubex Status",
                        children: statusChildren
                    });
                }
            }

            return options;
        },
        appliedFiltersCount() {
            return this.selectedRecordFilters.length;
        }
    },
    watch: {
        showFilterPopover(isShown) {
            if (isShown) {
                document.addEventListener(
                    "click",
                    this.handleClickOutside,
                    true
                );
            } else {
                document.removeEventListener(
                    "click",
                    this.handleClickOutside,
                    true
                );
            }
        }
    },
    created() {
        this.debouncedGetList = debounce(this.getList, 800);
        this.fetchDispensingStatuses();
    },
    mounted() {
        this.getList();
        this.isNewLabProfile();

        this.$root.$on("antech-status-update", this.getList);
    },
    beforeDestroy() {
        this.list = [];
        this.meta = null;

        this.$root.$off("antech-status-update", this.getList);
        document.removeEventListener("click", this.handleClickOutside, true);

        // Clear any pending timeout
        if (this.clickOutsideTimeout) {
            clearTimeout(this.clickOutsideTimeout);
        }
    },
    sockets: {
        "v-broadcast"(payload) {
            if (
                payload.action === "patient-history-refresh" &&
                payload.patientId === this.patientId
            ) {
                this.getList();
            }
        }
    },
    methods: {
        toggleFilterPopover() {
            this.showFilterPopover = !this.showFilterPopover;
        },
        clearRecordFiltersAndClosePopover() {
            this.clearRecordFilters();
            this.showFilterPopover = false;
        },
        handleClickOutside(event) {
            // Ignore click outside detection if we're in the middle of a filter change
            if (this.isFilterChanging) {
                return;
            }

            // Clear any existing timeout
            if (this.clickOutsideTimeout) {
                clearTimeout(this.clickOutsideTimeout);
            }

            // Set a new timeout
            this.clickOutsideTimeout = setTimeout(() => {
                const popover = this.$refs.filterPopover;
                const button = document.getElementById("recordFilterButton");

                if (popover && button) {
                    if (
                        !popover.$el.contains(event.target) &&
                        !button.contains(event.target) &&
                        popover.localShow
                    ) {
                        const treeselectMenu = document.querySelector(
                            ".vue-treeselect__menu-container"
                        );
                        if (
                            treeselectMenu &&
                            treeselectMenu.contains(event.target)
                        ) {
                            return;
                        }
                        this.showFilterPopover = false;
                    }
                }
            }, 100);
        },
        async fetchDispensingStatuses() {
            try {
                const response = await History.getItemDispensingStatuses(
                    this.patientId
                );
                if (response && response.resources) {
                    this.dispensingStatuses = response.resources;
                } else {
                    this.dispensingStatuses = [];
                }
            } catch (error) {
                this._message("Error fetching filter statuses", "danger");
                this.dispensingStatuses = [];
            }
        },
        updateInvoiceStatus(recordId, invoiceResponse) {
            let row = find(this.list, { id: recordId });
            row.invoice_item.invoice = {
                ...row.invoice_item.invoice,
                ...invoiceResponse
            };
        },
        expandSummary(id) {
            this.contentExpandedId = id;
        },
        getPatientOwnerId() {
            const { activeOwners } = this.patientDetails;
            if (activeOwners) {
                return activeOwners[0]?.id;
            } else {
                return null;
            }
        },
        viewRecord(record) {
            Records.showRecordDetails(record.id, record.business_id).then(
                response => {
                    if (response) {
                        this.$refs.modalViewRecord.open({
                            ...record,
                            record_resources: response
                        });
                    }
                }
            );
        },
        editRecord(record) {
            this.selectedRowId = record.id;
            let date = this._fromDateCarbon(record.date).toSeconds();
            if (record.invoice_item && record.invoice_item.date) {
                if (
                    this._fromDateCarbon(record.invoice_item.date) <
                    this._fromDateCarbon(record.date)
                ) {
                    date = this._fromDateCarbon(
                        record.invoice_item.date
                    ).toSeconds();
                }
            }

            this.$store.dispatch("checkBillingLock", {
                date,
                onProceed: () => {
                    Records.editRecordDetails(record.id).then(response => {
                        if (response) {
                            const editedRecord = {
                                ...record,
                                clientID: this.getPatientOwnerId(),
                                action: "edit",
                                module: this.MODULE.PATIENT,
                                record_resources: response
                            };
                            if (this.isSoap) {
                                // VET-5725 : From soap record tab trigger open modal from the parent component to avoid closing the modal when changing tab
                                this.$root.$emit("open-medical-record-modal", {
                                    recordParams: editedRecord
                                });
                            } else {
                                this.$refs.modalRecord.open(editedRecord);
                            }
                        }
                        this.selectedRowId = null;
                    });
                },
                onError: message => this._message(message, "danger")
            });
        },
        duplicateRecord(record) {
            this.selectedRowId = record.id;
            Records.duplicateRecordDetails(record.id).then(response => {
                if (response) {
                    if (!response.is_parent_item && response.canDuplicate) {
                        this.$refs.modalRecord.open({
                            ...record,
                            clientID: this.getPatientOwnerId(),
                            action: "duplicate",
                            module: this.MODULE.PATIENT,
                            record_resources: response
                        });
                    } else {
                        this._message(response.message, "danger");
                    }
                }
                this.selectedRowId = null;
            });
        },
        declinedItemRecord(record) {
            this.invoiceItem = {
                id: record.invoice_item.id,
                item_name: record.item_name,
                invoice_item: record.invoice_item,
                total: record.invoice_item.total
            };

            let date = record.invoice_item.date
                ? this._fromDateCarbon(record.invoice_item.date).toFormat("X")
                : this._fromDateCarbon(record.create_at).toFormat("X");

            this.$store.dispatch("checkBillingLock", {
                date,
                onProceed: () => this.$refs.modalDeclineItem.open(),
                onError: message => this._message(message, "danger")
            });
        },
        refillRecord(record) {
            this.selectedRowId = record.id;
            const recordId = record.refill_pid ? record.refill_pid : record.id;

            Records.refillRecordDetails(recordId).then(response => {
                if (response) {
                    if (response.canRefill == 1) {
                        this.$refs.modalRecord.open({
                            ...record,
                            clientID: this.getPatientOwnerId(),
                            action: "refill",
                            module: this.MODULE.PATIENT,
                            record_resources: response
                        });
                    } else {
                        this._message(response.message, "danger");
                    }
                }
                this.selectedRowId = null;
            });
        },
        printLabel(record) {
            let recordType = null;

            if (record && record.type) {
                recordType = record.type;
            }

            this.$refs.modalCageCard.open({
                action: "PRINT_LABEL",
                record_id: record.id,
                record_type: recordType // record.item.item_type.id
            });
        },
        getList(page = 1, refreshProfile = false) {
            if (this.apiCancelToken) {
                this.apiCancelToken.cancel(
                    "Operation canceled due to new request."
                );
            }

            this.apiCancelToken = axios.CancelToken.source();

            this.isLoading = true;

            let params = {
                page: page,
                perPage: this.PER_PAGE,
                type: "record",
                category: this.category ? this.category : ""
            };

            if (this.isSoap) {
                params.soapId = this.soapDetails?.id;
            }

            const selectedSources = this.selectedRecordFilters
                .filter(f => f.startsWith("source|"))
                .map(f => f.split("|")[1]);

            const selectedStatuses = this.selectedRecordFilters
                .filter(f => f.startsWith("status|"))
                .map(f => f.split("|")[1]);

            if (
                this.category == this.PHARMACY_ID &&
                selectedSources.length > 0
            ) {
                params.source = selectedSources.join(",");
            }

            if (selectedStatuses.length > 0) {
                params.item_status = selectedStatuses.join(",");
            }

            History.getAllHistory(
                this.patientId,
                params,
                this.apiCancelToken.token
            )
                .then(response => {
                    if (response.message.code == 200) {
                        if (refreshProfile) {
                            this.$emit("refresh-profile");
                        }
                        this.list = response.resources;

                        // Update categoryRecordCount if category is active and count is provided
                        if (
                            this.category &&
                            response.category_count &&
                            typeof response.category_count.records !==
                                "undefined"
                        ) {
                            this.categoryRecordCount =
                                response.category_count.records;
                        } else {
                            this.categoryRecordCount = null;
                        }
                    } else {
                        this.showError();
                        this.categoryRecordCount = null;
                    }
                })
                .catch(error => {
                    // Only show error if it's not a canceled request
                    if (!axios.isCancel(error)) {
                        this.showError();
                    }
                    this.categoryRecordCount = null;
                })
                .finally(() => {
                    this.isLoading = false;
                    this.apiCancelToken = null;
                });
        },
        showError() {
            this._message(
                "Error : Unable to get medical record list",
                "danger"
            );
        },
        deleteRecord(row) {
            let date = row.date
                ? this._fromDateCarbon(row.date).toFormat("X")
                : null;

            this.$store.dispatch("checkBillingLock", {
                date,
                onProceed: () => this.$refs.modalDeleteRecord.open(row),
                onError: message => this._message(message, "danger")
            });
        },
        viewHistoryRecord(row) {
            this.$refs.modalViewHistory.open(row);
        },
        editNote(row) {
            window.location.href = process.env.CI_APPS + row.ci_url;
        },
        deleteNote(row) {
            this.$refs.modalDeleteNote.open(row);
        },
        duplicateNote(row) {
            this.$refs.modalDuplicateNote.open(row);
        },
        editDiagnosis(diagnosis) {
            let self = this;
            Records.getDiagnosisDetails(this.patientId, diagnosis.id).then(
                response => {
                    if (response.message.code === 200) {
                        self.$refs.modalEditDiagnosis.open({
                            action: "edit",
                            editRecord: response.resource
                        });
                    }
                }
            );
        },
        onSuccess(refreshProfile = false) {
            if (this.isSoap) {
                // for soap, it will emit the main file which is soap.vue to refresh list and count
                this.$emit("onSuccess");
            } else {
                this.$store.dispatch("patient/getHistoryCount", {
                    patientId: this.patientId
                });
                this.getList(1, refreshProfile);
            }
        },
        async isNewLabProfile() {
            let res = await AccountService.checkFeature("lab_profile");
            let isEnabled = res?.message?.code;

            if (isEnabled === 404) {
                this.isNewLab = true;
            }
        },
        reloadTabCount() {
            this.$emit("reload-tab-count");
        },
        addAddonTest(row) {
            this.$refs.modalAddAddonTest.open({
                lab_pid: row.lab_pid,
                id: row.id
            });
        },
        handleFilterUpdate() {
            this.isFilterChanging = true;

            // Get currently selected status filters
            const selectedStatuses = this.selectedRecordFilters
                .filter(f => f.startsWith("status|"))
                .map(f => f.split("|")[1]);

            // Get currently selected source filters
            const selectedSources = this.selectedRecordFilters
                .filter(f => f.startsWith("source|"))
                .map(f => f.split("|")[1]);

            // If any status is selected and Cubex is not selected, add Cubex
            if (
                selectedStatuses.length > 0 &&
                !selectedSources.includes("CUBEX")
            ) {
                // Remove any existing source filters
                this.selectedRecordFilters = this.selectedRecordFilters.filter(
                    f => !f.startsWith("source|")
                );
                // Add Cubex source
                this.selectedRecordFilters.push("source|CUBEX");
            }

            this.debouncedGetList(1);

            // Reset the flag after a short delay to allow the popover to stabilize
            setTimeout(() => {
                this.isFilterChanging = false;
            }, 300);
        },
        clearRecordFilters() {
            this.selectedRecordFilters = [];
            this.getList(1); // Refresh list and go to page 1
        }
    }
};
</script>
<style scoped>
.record-filter-popover {
    min-width: 280px;
}
.record-filter-treeselect-in-popover >>> .vue-treeselect__control {
    /* Styles for the treeselect control if needed when inside popover */
}

.record-filter-popover >>> .popover-body {
    padding-top: 0.5rem;
}
</style>
